import AntSwitch from '@/components/form-control/AntSwitch'
import InputLabelField from '@/components/form-control/InputLabelField'
import { ArrowDownIcon, ArrowUpIcon, LeftArrow } from '@/components/icons'
import { useAuthContext } from '@/features/auth/context'
import { usePermission } from '@/hooks/permissions'
import useTranslation from '@/hooks/useTranslation'
import { useUnits } from '@/hooks/useUnits'
import { UpdateFlowRule } from '@/types/hydraulic'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Fade,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import clsx from 'clsx'
import { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useConfigUI } from '../../context'
import useAddFlowRule from '../../hooks/hydraulic/flowRule/useAddFlowRule'
import useSearchFlowSrcId from '../../hooks/hydraulic/flowRule/useGetFlowSrcId'
import useSearchFlowRuleId from '../../hooks/hydraulic/flowRule/useSearchFlowRuleId'
import useUpdateFlowRule from '../../hooks/hydraulic/flowRule/useUpdateFlowRule'
import useSearchHydraulicId from '../../hooks/hydraulic/useSearchHydraulicId'
import { CONFIG_ROUTES } from '../../routes'
import FlowSensorView from './FlowSensor'
import FlowValveView from './FlowValve'
import { schema } from './constant'
import classes from './style.module.scss'
import AutoActionPopover from './AutoActionPopover'

const HydraulicRuleView = () => {
  // form
  const form = useForm<UpdateFlowRule>({
    resolver: yupResolver(schema as any),
  })
  const { t } = useTranslation()
  const { interfaceId } = useAuthContext()
  const { pipeId } = useParams<{ pipeId: string; ruleId: string }>()
  const { reset, handleSubmit } = form
  const { data, isLoading: isLoadingHydraulic } = useSearchHydraulicId(
    Number(interfaceId),
    Number(pipeId),
  )
  const { convertLabelUnit, convertFromMetricV2, convertToMetricV2 } =
    useUnits()

  const navigate = useNavigate()
  const [open, setOpen] = useState<boolean>(true)

  //add
  const { mutate: addFlowRule, isLoading: loadingAddRule } = useAddFlowRule()
  const { data: newRuleId } = useSearchFlowSrcId(Number(interfaceId))

  //update
  const { data: dataRuleDetail } = useSearchFlowRuleId(
    Number(interfaceId),
    Number(data?.flowRuleId),
  )
  const { mutate: updateFlowRule, isLoading: loadingEditRule } =
    useUpdateFlowRule()

  //delete
  const { openModal, setModalView } = useConfigUI()
  const { isViewOnly } = usePermission()

  const handleSave = (values: UpdateFlowRule) => {
    if (isViewOnly) return false

    if (data?.flowRuleId) {
      //Edit
      updateFlowRule(
        {
          allowedIdleFlow: convertToMetricV2(
            values?.allowedIdleFlow || 0,
            'l/m',
            'gpm',
            2,
          ),
          id: values.id,
          interfaceId: Number(interfaceId),
          pipeId: values.pipeId,
          active: values.active,
          maxLowDeviation: values.maxLowDeviation,
          maxHighDeviation: values.maxHighDeviation,
          startDelay: values.startDelay,
          changeDelay: values.changeDelay,
          errorDelay: values.errorDelay,
          stableDeviation: values.stableDeviation,
          stableCount: values.stableCount,
          stableMax: values.stableMax,
          autoAction: values.autoAction,
        },
        {
          onSuccess: () => {
            navigate(CONFIG_ROUTES.dataConfig.to)
          },
        },
      )
    } else {
      //Add
      addFlowRule(
        {
          allowedIdleFlow: convertToMetricV2(
            values?.allowedIdleFlow || 0,
            'l/m',
            'gpm',
            2,
          ),
          interfaceId: Number(interfaceId),
          pipeId: values.pipeId,
          active: values.active,
          maxLowDeviation: values.maxLowDeviation,
          maxHighDeviation: values.maxHighDeviation,
          startDelay: values.startDelay,
          changeDelay: values.changeDelay,
          errorDelay: values.errorDelay,
          stableDeviation: values.stableDeviation,
          stableCount: values.stableCount,
          stableMax: values.stableMax,
          autoAction: values.autoAction,
        },
        {
          onSuccess: () => {
            navigate(CONFIG_ROUTES.dataConfig.to)
          },
        },
      )
    }
  }

  const allowedIdleFlowConverted = useMemo(() => {
    if (!dataRuleDetail?.allowedIdleFlow) return 0
    return convertFromMetricV2(dataRuleDetail.allowedIdleFlow, 'l/m', 'gpm', 2)
  }, [dataRuleDetail?.allowedIdleFlow, convertFromMetricV2])

  useEffect(() => {
    if (dataRuleDetail) {
      reset({
        ...dataRuleDetail,
        allowedIdleFlow: allowedIdleFlowConverted,
      })
    } else {
      reset({
        pipeId: data?.id,
        interfaceId: Number(interfaceId),
        id: newRuleId,
        active: true,
        autoAction: false,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataRuleDetail, newRuleId, allowedIdleFlowConverted])

  const handleDeleteRule = (id: number) => {
    if (isViewOnly) return false

    openModal()
    setModalView({
      type: 'DELETE_FLOW_RULE_VIEW',
      id: id.toString(),
      interfaceId: interfaceId + '',
      name: data?.description,
    })
  }

  return (
    <FormProvider {...form}>
      {isLoadingHydraulic && (
        <Stack
          sx={{
            position: 'absolute',
            transform: 'translate(-50%, -50%)',
            left: '50%',
            top: '50%',
          }}
        >
          <CircularProgress />
        </Stack>
      )}
      {!isLoadingHydraulic && (
        <Fade in={!isLoadingHydraulic}>
          <form className={classes.root} onSubmit={handleSubmit(handleSave)}>
            <Stack className={classes.wrapper}>
              <Stack className={classes.header}>
                <Stack
                  className={classes.arrowLeftIcon}
                  component={Link}
                  to={CONFIG_ROUTES.dataConfig.to}
                >
                  <LeftArrow />
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Typography className={classes.headTitle}>
                    {`${t('dataConfigFlowrule')} - `}
                  </Typography>
                  <Typography className={classes.txtDescription}>
                    {data?.description}
                  </Typography>
                </Stack>
              </Stack>

              <Stack className={classes.content}>
                <Stack className={classes.overallConfig}>
                  <Stack className={classes.overallHead}>
                    <Stack className={classes.leftHeader}>
                      <Typography className={classes.titleOverall}>
                        {t('overallConfiguration')}
                      </Typography>
                      <IconButton
                        aria-label="expand row"
                        sx={{ padding: 0 }}
                        onClick={() => setOpen(!open)}
                      >
                        {open ? (
                          <ArrowUpIcon sx={{ color: 'text.main' }} />
                        ) : (
                          <ArrowDownIcon sx={{ color: 'text.main' }} />
                        )}
                      </IconButton>
                    </Stack>

                    <Stack className={classes.rightHeader}>
                      {!isViewOnly && dataRuleDetail && (
                        <Button
                          className={classes.deleteBtn}
                          size="small"
                          sx={{ marginRight: '7px', width: '85px' }}
                          onClick={() =>
                            handleDeleteRule(Number(dataRuleDetail?.id))
                          }
                        >
                          {t('actionDelete')}
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        size="small"
                        sx={{ marginRight: '7px', width: '85px' }}
                        component={Link}
                        to={CONFIG_ROUTES.dataConfig.to}
                      >
                        {t('commonActionsCancel')}
                      </Button>

                      {isViewOnly ? null : (
                        <LoadingButton
                          size="small"
                          loading={
                            data?.flowRuleId ? loadingEditRule : loadingAddRule
                          }
                          variant="primary"
                          sx={{ minWidth: '72px' }}
                          type="submit"
                        >
                          {t('commonActionsSave')}
                        </LoadingButton>
                      )}
                    </Stack>
                  </Stack>

                  <Collapse
                    in={open}
                    timeout="auto"
                    unmountOnExit
                    sx={{
                      padding: '16px',
                      border: '1px solid #D7D7D7',
                      borderTop: 'unset',
                    }}
                  >
                    <Stack className={classes.containerOveral}>
                      <Stack className={classes.overalRight}>
                        <Stack className={clsx(classes.boxOne)}>
                          <Box
                            bgcolor="background.overrideDefault"
                            className={classes.groupActived}
                          >
                            <Typography className={classes.textActived}>
                              Active
                            </Typography>
                            <AntSwitch
                              form={form}
                              name="active"
                              width={36}
                              height={19.2}
                              widthThumb={14.4}
                              heightThumb={14.4}
                              borderThumb={14.4}
                              borderTrack={19 / 2}
                              transform={'16'}
                              disabled={isViewOnly}
                              sx={{
                                ...(isViewOnly && {
                                  pointerEvents: 'none',
                                  opacity: 0.4,
                                }),
                              }}
                            />
                          </Box>
                          <Box
                            bgcolor="background.overrideDefault"
                            className={classes.groupAutomatic}
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography className={classes.textActived}>
                                Automatic Action
                              </Typography>
                              <AutoActionPopover />
                            </Box>
                            <AntSwitch
                              form={form}
                              name="autoAction"
                              width={36}
                              height={19.2}
                              widthThumb={14.4}
                              heightThumb={14.4}
                              borderThumb={14.4}
                              borderTrack={19 / 2}
                              transform={'16'}
                              disabled={isViewOnly}
                              sx={{
                                ...(isViewOnly && {
                                  pointerEvents: 'none',
                                  opacity: 0.4,
                                }),
                              }}
                            />
                          </Box>
                        </Stack>{' '}
                        <Stack>
                          <InputLabelField
                            name="state"
                            label="State"
                            placeholder="State"
                            value={
                              dataRuleDetail?.state === 0
                                ? 'OK'
                                : 'Unexpected flow'
                            }
                            style={{
                              borderRadius: '8px',
                            }}
                            disabled={true}
                          />
                        </Stack>
                        <Stack>
                          <InputLabelField
                            name="id"
                            form={form}
                            label="ID"
                            style={{
                              borderRadius: '8px',
                            }}
                            disabled={true}
                          />
                        </Stack>
                      </Stack>

                      <Stack className={classes.divider}></Stack>

                      <Stack className={classes.overalRight}>
                        <Stack>
                          <InputLabelField
                            isRequired
                            name="allowedIdleFlow"
                            form={form}
                            label={`Allowed Idle Flow (${convertLabelUnit(
                              'l/m',
                            )})`}
                            placeholder={`Allowed Idle Flow (${convertLabelUnit(
                              'l/m',
                            )})`}
                            style={{
                              borderRadius: '8px',
                              opacity: isViewOnly ? 0.6 : 1,
                            }}
                            disabled={isViewOnly}
                          />
                        </Stack>

                        <Stack>
                          <InputLabelField
                            isRequired
                            name="maxLowDeviation"
                            form={form}
                            label="Max Low Deviation (%)"
                            placeholder="Max Low Deviation (%)"
                            style={{
                              borderRadius: '8px',
                              opacity: isViewOnly ? 0.6 : 1,
                            }}
                            disabled={isViewOnly}
                          />
                        </Stack>

                        <Stack>
                          <InputLabelField
                            isRequired
                            name="maxHighDeviation"
                            form={form}
                            label="Max High Deviation (%)"
                            placeholder="Max High Deviation (%)"
                            style={{
                              borderRadius: '8px',
                              opacity: isViewOnly ? 0.6 : 1,
                            }}
                            disabled={isViewOnly}
                          />
                        </Stack>
                      </Stack>

                      <Stack className={classes.divider}></Stack>

                      <Stack className={classes.overalRight}>
                        <Stack>
                          <InputLabelField
                            isRequired
                            name="startDelay"
                            form={form}
                            label="Start Delay (sec)"
                            placeholder="Start Delay (sec)"
                            style={{
                              borderRadius: '8px',
                              opacity: isViewOnly ? 0.6 : 1,
                            }}
                            disabled={isViewOnly}
                          />
                        </Stack>

                        <Stack>
                          <InputLabelField
                            isRequired
                            name="changeDelay"
                            form={form}
                            label="Change Delay (sec)"
                            placeholder="Change Delay (sec)"
                            style={{
                              borderRadius: '8px',
                              opacity: isViewOnly ? 0.6 : 1,
                            }}
                            disabled={isViewOnly}
                          />
                        </Stack>

                        <Stack>
                          <InputLabelField
                            isRequired
                            name="errorDelay"
                            form={form}
                            label="Error Delay (sec)"
                            placeholder="Error Delay (sec)"
                            style={{
                              borderRadius: '8px',
                              opacity: isViewOnly ? 0.6 : 1,
                            }}
                            disabled={isViewOnly}
                          />
                        </Stack>
                      </Stack>

                      <Stack className={classes.divider}></Stack>

                      <Stack className={classes.overalRight}>
                        <Stack>
                          <InputLabelField
                            isRequired
                            name="stableDeviation"
                            form={form}
                            label="Stable Deviation (%)"
                            placeholder="Stable Deviation (%)"
                            style={{
                              borderRadius: '8px',
                              opacity: isViewOnly ? 0.6 : 1,
                            }}
                            disabled={isViewOnly}
                          />
                        </Stack>

                        <Stack>
                          <InputLabelField
                            isRequired
                            name="stableCount"
                            form={form}
                            label="Stable Count"
                            placeholder="Stable Count"
                            style={{
                              borderRadius: '8px',
                              opacity: isViewOnly ? 0.6 : 1,
                            }}
                            disabled={isViewOnly}
                          />
                        </Stack>

                        <Stack>
                          <InputLabelField
                            isRequired
                            name="stableMax"
                            form={form}
                            label="Stable Max (min)"
                            placeholder="Stable Max (min)"
                            style={{
                              borderRadius: '8px',
                              opacity: isViewOnly ? 0.6 : 1,
                            }}
                            disabled={isViewOnly}
                          />
                        </Stack>
                      </Stack>
                    </Stack>
                  </Collapse>
                </Stack>

                {dataRuleDetail && (
                  <>
                    <FlowValveView flowRuleId={dataRuleDetail?.id} />
                    <FlowSensorView flowRuleId={dataRuleDetail?.id} />
                  </>
                )}
              </Stack>
            </Stack>
          </form>
        </Fade>
      )}
    </FormProvider>
  )
}

export default HydraulicRuleView
